import React, { useEffect, useState, Fragment, forwardRef } from "react";
import { useItemConfigContext } from "../../hooks/useContext/useItemConfigContext";
import { useAuthContext } from "../../hooks/useContext/useAuthContext";
import { styled } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import ImageContainer from "../Shared/ImageContainerBox";
import ErrorBox from "../Shared/ErrorBox";
import {
  Card,
  Collapse,
  CardContent,
  Typography,
  Tooltip,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  TextField,
  FormControl,
  Input,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import theme from "../../theme";
import { useTranslation } from "../../hooks/useTranslation";

const RootCard = styled(Card)({
  height: "100%",
  backgroundColor: "white",
  p: 2,
  borderRadius: "8px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  overflowWrap: "break-word",
  position: "relative",
});

const CustomDelete = styled(Delete)({
  color: theme.palette.common.error,
});

const CustomEdit = styled(Edit)({
  color: theme.palette.common.offGrey,
});

const InputContainer = styled(Input)({
  padding: "6px 12px",
  margin: ".8rem",
});

const ItemConfigDetails = ({ itemConfig }) => {
  const theme = useTheme();
  const { t, language } = useTranslation();
  const { dispatch } = useItemConfigContext();
  const { user } = useAuthContext();

  // Get translated item name
  const displayItemName = language === 'pl' ? (t(itemConfig.itemName) || itemConfig.itemName) : itemConfig.itemName;

  const translateSize = (size) => {
    if (size === 'Small') return t('small');
    if (size === 'Large') return t('large');
    if (size === 'One Size') return t('oneSize');
    return size;
  };

  const translateSizeOption = (option) => {
    if (option === 'One Size') return t('oneSize');
    if (option === 'Large & Small') return t('largeAndSmall');
    return option;
  };

  const getSubcategoryKey = (subcategory) => {
    const keyMap = {
      "Main Plates": "mainPlates",
      "Snacks & Fries": "snacksAndFries",
      "Burgers & Kebab": "burgersAndKebab",
      "Soups": "soups",
      "Pierogi & Regional": "pierogiAndRegional",
      "Desserts": "desserts",
      "Sweets": "sweets",
      "Hot": "hot",
      "Cold": "cold",
      "Alcoholic": "alcoholic",
      "Shots": "shots",
    };
    return keyMap[subcategory] || subcategory;
  };
  const [editedItemImage, setEditedItemImage] = useState(null);
  const [editedItemIncludes, setEditedItemIncludes] = useState(itemConfig.itemIncludes ? itemConfig.itemIncludes : "");
  const [editedItemAvailable, setEditedItemAvailable] = useState(itemConfig.itemAvailable ? itemConfig.itemAvailable : false);
  const [editedItemSize, setEditedItemSize] = useState(itemConfig.itemDetails.length === 2 ? "Large & Small" : "One Size");
  const [itemPrice1, setItemPrice1] = useState(itemConfig.itemDetails.length > 0 ? itemConfig.itemDetails[0].itemPrice : "");
  const [itemPrice2, setItemPrice2] = useState(itemConfig.itemDetails.length > 1 ? itemConfig.itemDetails[1].itemPrice : "");
  const [editedItemName, setEditedItemName] = useState(itemConfig.itemName ? itemConfig.itemName : "");
  const [editedItemType, setEditedItemType] = useState(itemConfig.itemType ? itemConfig.itemType : "Food");
  const [editMode, setEditMode] = useState(false);
  const [open, setOpen] = useState(false);
  const [expandedItemDetails, setExpandedItemDetails] = useState(false);
  const itemSizeOptions = ["One Size", "Large & Small"];
  const itemAvailableOptions = ["No", "Yes"];
  const [editedItemSubcategory, setEditedItemSubcategory] = useState(
    itemConfig.itemSubcategory ? itemConfig.itemSubcategory : ""
  );

  const itemTypeOptions = ["Food", "Grill", "Drink"];

  const subcategoryOptions = {
    Food: [
      "Main Plates",
      "Snacks & Fries",
      "Burgers & Kebab",
      "Soups",
      "Pierogi & Regional",
      "Desserts",
      "Sweets",
    ],
    Grill: [
      "Main Plates",
      "Snacks & Fries",
      "Burgers & Kebab",
      "Soups",
      "Pierogi & Regional",
      "Desserts",
      "Sweets",
    ],
    Drink: [
      "Hot",
      "Cold",
      "Alcoholic",
      "Shots",
    ],
  };
  const [itemPriceAmount, setItemPriceAmount] = useState(false);
  let newItemDetails;
  
  const [error, setError] = useState(null);
  const [emptyFields, setEmptyFields] = useState([]);
  const [itemPrice1Error, setItemPrice1Error] = useState(null);
  const [itemPrice2Error, setItemPrice2Error] = useState(null);

  const SolidLineDiv = styled("div")({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "4px",
    backgroundColor: itemConfig.itemAvailable ? theme.palette.component.componentBackground : theme.palette.common.errorDarker,
  });

  const ForwardedCollapse = forwardRef((props, ref) => (
    <Collapse ref={ref} {...props} />
  ));

  const handleExpandItemDetailsClick = () => {
    setExpandedItemDetails(!expandedItemDetails);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  //render large item price input field if item size is Large & Small
  useEffect(() => {
    if (editedItemSize === "Large & Small") {
      setItemPriceAmount(false);
      if (!itemPrice2) {
        setItemPrice2("");
      }
    } else {
      setItemPriceAmount(true);
    }
  }, [editedItemSize, itemPrice2]);

  //useEffect to check if itemPrice1 and itemPrice2 is a number
  useEffect(() => {
    if (isNaN(itemPrice1)) {
      setItemPrice1Error("Please enter a valid number");
      setEmptyFields((emptyFields) => [...emptyFields, "itemPrice1"]);
    } else {
      setItemPrice1Error(null);
      setEmptyFields((emptyFields) =>
        emptyFields.filter((field) => field !== "itemPrice1")
      );
    }
    if (isNaN(itemPrice2) && editedItemSize === "Large & Small") {
      setItemPrice2Error("Please enter a valid number");
      setEmptyFields((emptyFields) => [...emptyFields, "itemPrice2"]);
    } else {
      setItemPrice2Error(null);
      setEmptyFields((emptyFields) =>
        emptyFields.filter((field) => field !== "itemPrice2")
      );
    }
  }, [itemPrice1, itemPrice2, editedItemSize]);

  if (!user) {
    return null;
  }

  const handleDelete = async () => {
    const response = await fetch(
      process.env.REACT_APP_SERVER_URL + "/api/itemconfigs/" + itemConfig._id,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${user.token}`},
      }
    );
    const json = await response.json();

    if (response.ok) {
      dispatch({ type: "DELETE_ITEMCONFIG", payload: json });
    }
  };

  //cancel button on edit
  const handleEditClick = () => {
    if (editMode === true) {
      setEditMode(false);
      setEditedItemName(itemConfig.itemName);
      setEditedItemSize(itemConfig.itemDetails.length === 2 ? "Large & Small" : "One Size");
      setItemPrice1(itemConfig.itemDetails.length > 0 ? itemConfig.itemDetails[0].itemPrice : "");
      setItemPrice2(itemConfig.itemDetails.length > 1 ? itemConfig.itemDetails[1].itemPrice : "");
      setEditedItemIncludes(!itemConfig.itemIncludes ? "" : itemConfig.itemIncludes);
      setEditedItemAvailable(itemConfig.itemAvailable);
      setEditedItemType(itemConfig.itemType ? itemConfig.itemType : "Food");
      setEditedItemSubcategory(itemConfig.itemSubcategory ? itemConfig.itemSubcategory : "");
    } else if (editMode === false) {
      setEditMode(true);
    }
  };

  const handleSaveClick = async (e) => {
    e.preventDefault();
    if (!user) {
      setError("You must be logged in");
      return;
    }
    let emptyFieldsClientSide = [];
    if (!editedItemName) {
      emptyFieldsClientSide.push("editedItemName");
      setError("Please set name");
    }
    if (!itemPrice1) {
      emptyFieldsClientSide.push("itemPrice1");
      setError("Please set price");
    }
    if (editedItemSize === "Large & Small" && !itemPrice2) {
      emptyFieldsClientSide.push("itemPrice2");
      setError("Please set a price for the large item");
    }
    if (!editedItemType) {
      emptyFieldsClientSide.push("itemType");
      setError("Please select food, grill or drink");
    }
    if (!editedItemSubcategory) {
      emptyFieldsClientSide.push("itemSubcategory");
      setError("Please select a subcategory");
    }
    if (emptyFieldsClientSide.length > 0) {
      setEmptyFields(emptyFieldsClientSide);
      if (emptyFieldsClientSide.length > 1) {
        setError("Please fill in all fields");
      }
      return;
    }

    if (editedItemSize !== "One Size") {
      newItemDetails = [
        {itemPrice: parseFloat(itemPrice1), itemSize: "Small"},
        {itemPrice: parseFloat(itemPrice2), itemSize: "Large"}
      ];
    } else {
      newItemDetails = [
        {itemPrice: parseFloat(itemPrice1), itemSize: "One Size"}
      ];
    }

  try {
    const formData = new FormData();
    if (editedItemImage) {
      formData.append("file", editedItemImage);
    }
    formData.append("itemName", editedItemName);
    formData.append("itemType", editedItemType);
    formData.append("itemSubcategory", editedItemSubcategory);
    formData.append('itemDetails', JSON.stringify(newItemDetails))
    formData.append("itemIncludes", editedItemIncludes);
    formData.append("itemAvailable", editedItemAvailable ? "true" : "false");

    const response = await fetch(
      process.env.REACT_APP_SERVER_URL +
        "/api/itemconfigs/update/" +
        itemConfig._id,
      {
        method: "PUT",
        body: formData,
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      }
    );
    const json = await response.json();

    if (!response.ok) {
      setError(json.error);
      setEmptyFields(json.emptyFieldsBackEnd);
      console.log(json.error);
    }

    if (response.ok) {
      setError(null);
      setEmptyFields([]);
      setEditMode(false);
      dispatch({ type: "UPDATE_ITEMCONFIG", payload: json });
    }
  } catch (error) {
    console.error(error);
    setError("An error occurred, please try again");
  }
};

  return (
    <RootCard variant="outlined">
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <IconButton
          sx={{
            color: theme.palette.component.componentBackground,
            backgroundColor: theme.palette.component.componentBackground,
            ":hover": { bgcolor: theme.palette.common.offWhite },
            }}
          size="large"
          aria-label="Edit item"
          aria-haspopup="true"
          onClick={handleEditClick}
        >
          <Tooltip title={t('editItem')} placement="top">
            <CustomEdit />
          </Tooltip>
        </IconButton>
        <IconButton
          sx={{
              color: theme.palette.component.componentBackground,
              backgroundColor: theme.palette.component.componentBackground,
              ":hover": { bgcolor: theme.palette.common.offWhite },
            }}
          size="large"
          aria-label="Delete"
          aria-haspopup="true"
          ariant="outlined"
          onClick={handleClickOpen}
        >
          <Tooltip title={t('delete')} placement="top">
            <CustomDelete />
          </Tooltip>
        </IconButton>
      </div>

      <CardContent onClick={handleExpandItemDetailsClick}>
        <SolidLineDiv />
        {editMode ? (
          <>
            <ImageContainer
              itemName={itemConfig.itemName}
              itemImage={editedItemImage ? URL.createObjectURL(editedItemImage) : itemConfig.itemImage}
            />
            <InputContainer
              type="file"
              onChange={(event) => {
                const file = event.target.files[0];
                if (file) {
                  setEditedItemImage(file);
                }
              }}
            />

            <TextField
              fullWidth
              required={true}
              label={t('itemName')}
              autoComplete="Item Name"
              type="text"
              onChange={(e) => setEditedItemName(e.target.value)}
              value={editedItemName}
              sx={{ marginTop: "0.5rem" }}
              error={emptyFields && emptyFields.includes("editedItemName")}
            />

            <FormControl
              required={true}
              fullWidth
              variant="outlined"
              margin="normal"
            >
              <InputLabel id="itemSize">{t('size')}</InputLabel>
              <Select
                labelId="itemSize"
                id="itemSize"
                value={editedItemSize ? editedItemSize : "One Size"}
                onChange={(e) => setEditedItemSize(e.target.value)}
                label={t('size')}
                error={emptyFields && emptyFields.includes("itemSize")}
              >
                <MenuItem value="">
                  <em>{t('sizeQuestion')}</em>
                </MenuItem>
                {itemSizeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {translateSizeOption(option)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              required={true}
              label={itemPriceAmount ? t('price') : t('priceOfSmallItem')}
              autoComplete="Price"
              type="currency"
              onChange={(e) => setItemPrice1(e.target.value)}
              value={itemPrice1}
              error={(emptyFields && emptyFields.includes("itemPrice1")) || Boolean(itemPrice1Error)}
              sx={{ marginTop: "0.5rem" }}
            />
            {itemPrice1Error && (
              <FormHelperText sx={{color: theme.palette.common.error}}>{itemPrice1Error}</FormHelperText>
            )}

            {!itemPriceAmount && (
              <TextField
                fullWidth
                required={true}
                label={t('priceOfLargeItem')}
                autoComplete="Price"
                type="currency"
                onChange={(e) => setItemPrice2(e.target.value)}
                value={itemPrice2}
                error={(emptyFields && emptyFields.includes("itemPrice2")) || Boolean(itemPrice2Error)}
                sx={{ marginTop: "1rem" }}
              />
            )}
            {itemPrice2Error && (
              <FormHelperText sx={{color: theme.palette.common.error}}>{itemPrice2Error}</FormHelperText>
            )}

            <TextField
              fullWidth
              required={false}
              label={t('itemDescription')}
              autoComplete="Includes"
              type="text"
              rows={4}
              multiline
              onChange={(e) => setEditedItemIncludes(e.target.value)}
              value={editedItemIncludes}
              sx={{ marginTop: "1rem" }}
            />

            <FormControl required={true} fullWidth variant="outlined" margin="normal">
              <InputLabel id="itemType">{t('itemType')}</InputLabel>
              <Select
                labelId="itemType"
                id="itemType"
                value={editedItemType}
                onChange={(e) => {
                  setEditedItemType(e.target.value);
                  setEditedItemSubcategory("");
                }}
                label={t('itemType')}
                error={emptyFields && emptyFields.includes("itemType")}
              >
                <MenuItem value="">
                  <em>{t('itemTypeQuestion')}</em>
                </MenuItem>
                {itemTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(option.toLowerCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              required={true}
              fullWidth
              variant="outlined"
              margin="normal"
              disabled={!editedItemType}
            >
              <InputLabel id="itemSubcategory">{t('subcategory')}</InputLabel>
              <Select
                labelId="itemSubcategory"
                id="itemSubcategory"
                value={editedItemSubcategory}
                onChange={(e) => setEditedItemSubcategory(e.target.value)}
                label={t('subcategory')}
                error={emptyFields && emptyFields.includes("itemSubcategory")}
              >
                <MenuItem value="">
                  <em>{editedItemType ? t('chooseSubcategory') : t('selectItemTypeFirst')}</em>
                </MenuItem>
                {editedItemType &&
                  subcategoryOptions[editedItemType]?.map((option) => (
                    <MenuItem key={option} value={option}>
                      {t(getSubcategoryKey(option))}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl
              required={true}
              fullWidth
              variant="outlined"
              margin="normal"
            >
              <InputLabel id="item-available">{t('itemAvailable')}</InputLabel>
              <Select
                labelId="itemAvailable"
                id="itemAvailable"
                value={editedItemAvailable ? "Yes" : "No"}
                onChange={(e) => setEditedItemAvailable(e.target.value === "Yes" ? true : false)}
                label={t('itemAvailable')}
                error={emptyFields && emptyFields.includes("itemAvailable")}
              >
                <MenuItem value="">
                  <em>{t('changeAvailability')}</em>
                </MenuItem>
                {itemAvailableOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === "Yes" ? t('yes') : t('no')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {error && <ErrorBox error={error} color={"error"} />}

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "10px",
              }}
            >
              <Button
                variant="contained"
                type="submit"
                sx={{ marginTop: "1rem",
                  color: theme.palette.common.white,
                  backgroundColor: theme.palette.primary.main,
                  ":hover": { bgcolor: theme.palette.secondary.main},
                }}
                onClick={handleSaveClick}
              >
                {t('saveChanges')}
              </Button>
              <Button
                variant="contained"
                type="submit"
                sx={{
                  color: theme.palette.common.black,
                  backgroundColor: theme.palette.common.background,
                  ":hover": { bgcolor: theme.palette.common.offGrey},
                  marginTop: "1rem",
                  marginLeft: "20px",
                  marginBottom: "0px",
                }}
                onClick={handleEditClick}
              >
                {t('cancel')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <Typography variant="h6" component="h2" sx={{ padding: '5px' }}>
              {displayItemName}
            </Typography>
            <ImageContainer itemName={itemConfig.itemName} itemImage={itemConfig.itemImage} />
            <ForwardedCollapse in={expandedItemDetails} sx={{padding: '5px'}}>
              <div>
                {itemConfig.itemDetails.map((detail, index) => (
                  <Fragment key={index}>
                    <Typography key={index} variant="body2" component="p" sx={{ display: 'flex', justifyContent: 'space-between'}}>
                      <span style={{padding: '5px' }}> <strong>{t('size')}: </strong>{translateSize(detail.itemSize)} </span>
                      <span style={{padding: '5px' }}> <strong> {t('price')}: </strong>{`${detail.itemPrice} ${t('currency')}`}</span>
                    </Typography>
                    <Divider sx={{ margin: "10px" }} variant="middle" />
                  </Fragment>
                ))}
                <Typography variant="body2" component="p" sx={{ display: 'flex', justifyContent: 'space-between'}}>
                  <span style={{padding: '5px' }}> <strong>{t('description')}: </strong> </span>
                  <span style={{padding: '5px' }}>{itemConfig.itemIncludes ? itemConfig.itemIncludes : t('notSet')}</span>
                </Typography>
                <Divider sx={{ margin: "10px" }} variant="middle" />
                <Typography variant="body2" component="p" sx={{ display: 'flex', justifyContent: 'space-between'}}>
                  <span style={{padding: '5px' }}> <strong>{t('itemType')}: </strong> </span>
                  <span style={{padding: '5px' }}>{itemConfig.itemType ? t(itemConfig.itemType.toLowerCase()) : t('notSet')}</span>
                </Typography>
                <Divider sx={{ margin: "10px" }} variant="middle" />
                <Typography variant="body2" component="p" sx={{ display: 'flex', justifyContent: 'space-between'}}>
                  <span style={{padding: '5px' }}> <strong>{t('itemSubcategory')}: </strong> </span>
                  <span style={{padding: '5px' }}>{itemConfig.itemSubcategory ? t(getSubcategoryKey(itemConfig.itemSubcategory)) : t('notSet')}</span>
                </Typography>
                <Divider sx={{ margin: "10px" }} variant="middle" />
                <Typography variant="body2" component="p" sx={{ display: 'flex', justifyContent: 'space-between'}}>
                  <span style={{padding: '5px' }}> <strong>{t('itemAvailable')} </strong> </span>
                  <span style={{padding: '5px' }}>{itemConfig.itemAvailable ? t('yes') : t('no')}</span>
                </Typography>
              </div>
            </ForwardedCollapse>
            <Button
              sx={{
                color: theme.palette.common.offGrey,
                backgroundColor: theme.palette.component.componentBackground,
                ":hover": { bgcolor: theme.palette.component.componentBackground },
              }}
              onClick={handleExpandItemDetailsClick}>
              {expandedItemDetails ? t('close') : t('seeMoreDetails')}
            </Button>
          </>
        )}
      </CardContent>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('deleteItem')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('areYouSureDelete')} <strong>{displayItemName}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ color: theme.palette.common.white,
              backgroundColor: theme.palette.common.error,
              ":hover": { bgcolor: theme.palette.common.errorDarker },
            }}
            onClick={handleDelete}
            autoFocus
          >
            {t('delete')}
          </Button>
          <Button
            sx={{ color: theme.palette.common.black,
                backgroundColor: theme.palette.common.background,
                ":hover": { bgcolor: theme.palette.common.offGrey},
              }}
            onClick={handleClose}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </RootCard>
  );
};

export default ItemConfigDetails;